import type { components } from "@selector/shared/openapi";
import type { GridApi, IRowNode } from "ag-grid-community";

import type { ColumnRow } from "../../ColumnSelectionGrid.tsx";
import { filterOutFrontendOnlyColumns, forEachRowNodeInStableOrder } from "../../utils";

type ColumnSparseResponse = components["schemas"]["ColumnSparseResponse"];

/**
 * Serializes the current AG Grid state back into the sparse column format expected by the backend.
 * Includes both pinned and scrollable rows so templates can round-trip accurately.
 */
export function collectColumnsFromGrid(api: GridApi<ColumnRow> | null): ColumnSparseResponse[] {
  if (!api) {
    return [];
  }

  const columns: ColumnSparseResponse[] = [];

  const appendColumn = (node: IRowNode<ColumnRow>) => {
    const column = toColumnResponse(node);
    if (column) {
      columns.push(column);
    }
  };

  api.forEachPinnedRow("top", appendColumn);
  const walked = forEachRowNodeInStableOrder(api, appendColumn);

  if (!walked) {
    api.forEachNodeAfterFilterAndSort(appendColumn);
  }

  return filterOutFrontendOnlyColumns(columns);
}

// Builds a template column node from an AG Grid row, recursing into children when the row is a group.
function toColumnResponse(node: IRowNode<ColumnRow>): ColumnSparseResponse | null {
  const colId = node.data?.colId;
  const title = node.data?.title ?? colId;

  if (!colId || !title) {
    return null;
  }

  const children =
    node.group || node.data?.isGroup
      ? node.allLeafChildren
          ?.map((leaf) => toLeafColumnResponse(leaf))
          .filter((child): child is ColumnSparseResponse => child != null)
      : undefined;

  const hasChildren = children && children.length > 0;
  const isFixed = Boolean(node.data?.fixed);
  const hasVisibleChildren = hasChildren ? children.some((c) => !c.hide) : false;
  const isExplicitlySelected = node.isSelected();

  return {
    colId,
    field: colId,
    fixed: isFixed,
    headerName: title,
    hide: isFixed ? false : !(isExplicitlySelected || hasVisibleChildren),
    children: hasChildren ? children : undefined,
  };
}

// Leaf nodes are simple one-to-one mappings from row data to sparse column definitions.
function toLeafColumnResponse(node: IRowNode<ColumnRow>): ColumnSparseResponse | null {
  const colId = node.data?.colId;
  const title = node.data?.title ?? colId;

  if (!colId || !title) {
    return null;
  }

  const isFixed = Boolean(node.data?.fixed);

  return {
    colId,
    field: colId,
    fixed: isFixed,
    headerName: title,
    hide: isFixed ? false : !node.isSelected(),
  };
}
